import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { LogOut, User, Menu, ChevronDown, Sun, Moon, Bell, Settings } from "lucide-react";
import { useAuth } from "@hooks/useAuth";
import { useNotificationStore } from "@stores/notifications/notificationStore";
import { useTheme } from "@stores/theme/useTheme";
import { clsx } from "clsx";
import {
    notificationsApi,
    type NotificationDto,
    type NotificationsListResponse,
} from "@api/modules/notifications.api";
import { authApi } from "@api/modules/auth.api";
import { formatDateTime } from "@lib/formatters";
import { LanguageMenu } from "./LanguageMenu";

interface OwnerHeaderProps {
    onMobileMenuToggle: () => void;
}

export const OwnerHeader: React.FC<OwnerHeaderProps> = ({ onMobileMenuToggle }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, clearAuth } = useAuth();
    const unreadCount = useNotificationStore((s) => s.unreadCount);
    const { theme, toggleTheme } = useTheme();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showBellMenu, setShowBellMenu] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);
    const bellMenuRef = useRef<HTMLDivElement>(null);
    const setUnreadCount = useNotificationStore((s) => s.setUnreadCount);

    const notificationsPreviewQuery = useQuery({
        queryKey: ["notifications", "preview", 5],
        queryFn: async () => {
            const res = await notificationsApi.list({ page: 1, perPage: 5 });
            const typed = res as NotificationsListResponse;
            setUnreadCount(typed.unreadCount ?? 0);
            return typed;
        },
        staleTime: 30_000,
        enabled: showBellMenu,
    });

    const previewNotifications: NotificationDto[] = notificationsPreviewQuery.data?.notifications ?? [];

    const handleLogout = () => {
        void authApi.logout().catch(() => {});
        clearAuth();
        navigate("/login");
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (userMenuRef.current && !userMenuRef.current.contains(target)) {
                setShowUserMenu(false);
            }
            if (bellMenuRef.current && !bellMenuRef.current.contains(target)) {
                setShowBellMenu(false);
            }
        };

        if (showUserMenu || showBellMenu) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showUserMenu, showBellMenu]);

    const openNotificationTarget = (n: NotificationDto) => {
        setShowBellMenu(false);
        if (n.url) {
            if (n.url.startsWith("http://") || n.url.startsWith("https://")) {
                window.location.assign(n.url);
            } else {
                const path = n.url.startsWith("/") ? n.url : `/${n.url}`;
                navigate(path);
            }
        } else {
            navigate("/notifications");
        }
    };

    const roleLabel = t(`nav.header.role.${user?.role ?? "fallback"}`, {
        defaultValue: t("nav.header.role.fallback"),
    });
    const userName = user?.name || t("nav.header.userFallback");

    return (
        <header className="sticky top-0 z-50 h-16 border-b border-neutral-border/50 bg-white/80 shadow-sm backdrop-blur-xl transition-all duration-300 dark:border-neutral-border/20 dark:bg-neutral-dark/80">
            <div className="flex h-full items-center justify-between px-4 lg:px-8">
                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        onClick={onMobileMenuToggle}
                        aria-label={t("nav.sidebar.openMenu")}
                        className="-ms-2 rounded-xl p-2 transition-colors duration-150 hover:bg-neutral-light/50 dark:hover:bg-white/5 lg:hidden"
                    >
                        <Menu size={22} className="text-neutral-dark dark:text-neutral-light" />
                    </button>
                    <div className="flex items-center gap-2">
                        <img src="/maeiz-icon.png" alt={t("common.brand")} className="ms-2 h-14 w-auto lg:ms-4" />
                        <span className="hidden font-display text-sm font-semibold text-neutral-dark/80 dark:text-white/90 sm:inline">
                            {t("common.platform")}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative" ref={bellMenuRef}>
                        <button
                            type="button"
                            onClick={() => {
                                setShowBellMenu((open) => !open);
                                setShowUserMenu(false);
                            }}
                            className="relative rounded-xl p-2 transition-colors duration-150 hover:bg-neutral-light/50 dark:hover:bg-white/5"
                            aria-label={t("nav.header.notifications")}
                            aria-expanded={showBellMenu}
                            aria-haspopup="menu"
                        >
                            <Bell size={20} className="text-neutral-dark dark:text-neutral-light" />
                            {unreadCount > 0 && (
                                <span className="absolute -end-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gold px-1 text-[11px] font-bold text-neutral-ink">
                                    {unreadCount > 99 ? "99+" : unreadCount}
                                </span>
                            )}
                        </button>
                        {showBellMenu && (
                            <div
                                className="absolute end-0 top-full z-[60] mt-3 w-[min(100vw-2rem,17.5rem)] rounded-xl border border-neutral-border/50 bg-white/95 py-2 shadow-xl shadow-black/10 backdrop-blur-xl dark:border-neutral-border/20 dark:bg-neutral-dark/95 dark:shadow-black/30 sm:w-64"
                                role="menu"
                            >
                                <div className="border-b border-neutral-border/30 px-3 py-2.5 dark:border-neutral-border/10">
                                    <p className="text-sm font-semibold text-neutral-dark dark:text-white">
                                        {t("nav.header.notifications")}
                                    </p>
                                </div>
                                <div className="max-h-[min(22rem,60vh)] overflow-y-auto">
                                    {notificationsPreviewQuery.isLoading ? (
                                        <div className="space-y-2 p-3">
                                            {[1, 2, 3].map((i) => (
                                                <div
                                                    key={i}
                                                    className="h-14 animate-pulse rounded-lg bg-neutral-light/80 dark:bg-white/10"
                                                />
                                            ))}
                                        </div>
                                    ) : previewNotifications.length === 0 ? (
                                        <p className="px-3 py-6 text-center text-sm text-neutral-dark/60 dark:text-neutral-light/55">
                                            {t("nav.header.noNotifications")}
                                        </p>
                                    ) : (
                                        <ul className="py-1">
                                            {previewNotifications.map((n) => (
                                                <li key={n.id}>
                                                    <button
                                                        type="button"
                                                        onClick={() => openNotificationTarget(n)}
                                                        className={clsx(
                                                            "flex w-full gap-3 px-3 py-2.5 text-start transition-colors hover:bg-neutral-light/70 dark:hover:bg-white/10",
                                                            !n.readAt && "bg-primary/[0.06] dark:bg-primary/10"
                                                        )}
                                                        role="menuitem"
                                                    >
                                                        <span
                                                            className={clsx(
                                                                "mt-1.5 h-2 w-2 flex-shrink-0 rounded-full",
                                                                n.readAt ? "bg-transparent" : "bg-tertiary"
                                                            )}
                                                            aria-hidden
                                                        />
                                                        <span className="min-w-0 flex-1">
                                                            <span className="line-clamp-1 text-sm font-semibold text-neutral-dark dark:text-white">
                                                                {n.title || t("nav.header.notificationFallback")}
                                                            </span>
                                                            {n.message ? (
                                                                <span className="mt-0.5 line-clamp-2 text-xs text-neutral-dark/70 dark:text-neutral-light/65">
                                                                    {n.message}
                                                                </span>
                                                            ) : null}
                                                            <span className="mt-1 block text-[11px] text-neutral-dark/45 dark:text-neutral-light/45">
                                                                {formatDateTime(n.createdAt)}
                                                            </span>
                                                        </span>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <div className="border-t border-neutral-border/30 p-2 dark:border-neutral-border/10">
                                    <Link
                                        to="/notifications"
                                        onClick={() => setShowBellMenu(false)}
                                        className="flex w-full items-center justify-center rounded-lg bg-primary/10 px-3 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/15 dark:bg-gold/15 dark:text-gold dark:hover:bg-gold/25"
                                        role="menuitem"
                                    >
                                        {t("nav.header.viewAll")}
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                    <LanguageMenu />
                    <button
                        type="button"
                        onClick={toggleTheme}
                        className="rounded-xl p-2 transition-colors duration-150 hover:bg-neutral-light/50 dark:hover:bg-white/5"
                        aria-label={t("nav.header.toggleTheme")}
                    >
                        {theme === "dark" ? (
                            <Sun size={20} className="text-neutral-dark dark:text-neutral-light" />
                        ) : (
                            <Moon size={20} className="text-neutral-dark dark:text-neutral-light" />
                        )}
                    </button>
                    <div className="relative overflow-visible" ref={userMenuRef}>
                        <button
                            type="button"
                            onClick={() => {
                                setShowUserMenu((open) => !open);
                                setShowBellMenu(false);
                            }}
                            className="flex items-center gap-2 rounded-xl border border-neutral-border/50 bg-gradient-to-r from-neutral-light to-neutral-light/50 px-2 py-1.5 pe-2.5 transition-all duration-200 hover:bg-black/[0.03] dark:border-neutral-border/20 dark:from-white/5 dark:to-white/[0.02] dark:hover:bg-white/[0.04]"
                            aria-expanded={showUserMenu}
                            aria-haspopup="menu"
                        >
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 ring-1 ring-primary/20 dark:from-primary/30 dark:to-secondary/30">
                                <User size={16} className="text-primary dark:text-white" />
                            </div>
                            <span className="hidden min-w-0 max-w-[160px] truncate text-start text-sm font-semibold text-neutral-dark dark:text-white sm:inline">
                                {userName}
                            </span>
                            <ChevronDown
                                size={14}
                                className={clsx(
                                    "flex-shrink-0 text-neutral-dark/40 transition-transform duration-200 dark:text-neutral-light/40",
                                    showUserMenu && "rotate-180"
                                )}
                            />
                        </button>
                        {showUserMenu && (
                            <div
                                className="absolute end-0 top-full z-[60] mt-2 w-[min(100vw-2rem,17.5rem)] rounded-xl border border-neutral-border/50 bg-white/95 py-2 shadow-xl shadow-black/10 backdrop-blur-xl dark:border-neutral-border/20 dark:bg-neutral-dark/95 dark:shadow-black/30 sm:w-64"
                                role="menu"
                            >
                                <div className="border-b border-neutral-border/30 px-3 pb-2.5 pt-1 dark:border-neutral-border/10">
                                    <p className="truncate text-sm font-semibold text-neutral-dark dark:text-white">
                                        {userName}
                                    </p>
                                    <p className="mt-1 text-xs text-neutral-dark/50 dark:text-neutral-light/50">
                                        {roleLabel}
                                    </p>
                                </div>
                                <div className="py-1">
                                    <Link
                                        to="/profile"
                                        onClick={() => setShowUserMenu(false)}
                                        className="mx-1 flex w-[calc(100%-0.5rem)] items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-neutral-dark transition-colors duration-200 hover:bg-neutral-light/70 dark:text-neutral-light dark:hover:bg-white/10"
                                        role="menuitem"
                                    >
                                        <User size={16} className="flex-shrink-0 text-neutral-dark/60 dark:text-neutral-light/60" />
                                        <span className="whitespace-nowrap">{t("nav.profile")}</span>
                                    </Link>
                                    <Link
                                        to="/settings"
                                        onClick={() => setShowUserMenu(false)}
                                        className="mx-1 flex w-[calc(100%-0.5rem)] items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-neutral-dark transition-colors duration-200 hover:bg-neutral-light/70 dark:text-neutral-light dark:hover:bg-white/10"
                                        role="menuitem"
                                    >
                                        <Settings size={16} className="flex-shrink-0 text-neutral-dark/60 dark:text-neutral-light/60" />
                                        <span className="whitespace-nowrap">{t("nav.settings")}</span>
                                    </Link>
                                </div>
                                <div className="mx-1 border-t border-neutral-border/30 dark:border-neutral-border/10" />
                                <button
                                    type="button"
                                    onClick={handleLogout}
                                    className="mx-1 flex w-[calc(100%-0.5rem)] items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-neutral-dark transition-colors duration-200 hover:bg-neutral-light/70 dark:text-neutral-light dark:hover:bg-white/10"
                                    role="menuitem"
                                >
                                    <LogOut size={16} className="flex-shrink-0 text-neutral-dark/60 dark:text-neutral-light/60" />
                                    <span className="whitespace-nowrap">{t("nav.header.signOut")}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};
